import React, { Fragment, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
    Button,
    Collapse,
    Grid,
    IconButton,
    styled,
    Tooltip,
} from '@mui/material';
import Icon from '@mdi/react';
import { mdiFilterVariant, mdiMagnify, mdiSwapHorizontal } from '@mdi/js';

import { useAppSelector } from '../../state/hooks';
import { AreaLabel } from '../../interfaces/services/areas';
import { buildAreaLink, buildLaneLink } from '../../utils/navigationHelpers';
import areas from '../../assets/data/areas.json';
import SelectAutocomplete from '../SelectAutocomplete/SelectAutocomplete';
import SearchFilterDrawer from '../Filters/SearchFilterDrawer';

const classesPrefix = 'laneSearch';

const classes = {
    container: `${classesPrefix}-container`,
    swapContainer: `${classesPrefix}-swapContainer`,
    icon: `${classesPrefix}-icon`,
    selectField: `${classesPrefix}-selectField`,
    btn: `${classesPrefix}-btn`,
    btnContainer: `${classesPrefix}-btnContainer`,
};

const StyledGrid = styled(Grid)(({ theme }) => {
    return {
        [`&.${classes.container}`]: {
            padding: `${theme.spacing(2)} ${theme.spacing(5)}`,
            backgroundColor: theme.palette.secondary.main,
            color: theme.palette.common.white,
            height: '100%',
        },
        [`& .${classes.swapContainer}`]: {
            margin: `0px ${theme.spacing(2)}`,
            display: 'flex',
            justifyContent: 'center',
        },
        [`& .${classes.icon}`]: {
            color: 'inherit',
        },
        [`& .${classes.selectField}`]: {
            minWidth: 250,
            '& .MuiInput-input': {
                color: theme.palette.common.white,
            },
            '& .MuiInput-underline': {
                '&:before': {
                    borderBottom: `1px solid ${theme.palette.common.white}`,
                },
                '&:hover:not(.Mui-disabled):before': {
                    borderBottom: `2px solid ${theme.palette.common.white}`,
                },
                '&.Mui-focused': {
                    borderBottom: `1px solid ${theme.palette.warning.dark}`,
                },
            },
            '& .MuiFormLabel-root': {
                color: theme.palette.common.white,
                '&.Mui-focused': {
                    color: theme.palette.warning.dark,
                },
            },
            '& .MuiInputBase-root': {
                color: theme.palette.common.white,
            },
            '& .MuiInputBase-input': {
                color: theme.palette.common.white,
                paddingLeft: '4px',
            },
            '& .MuiIconButton-root': {
                color: theme.palette.common.white,
            },
        },
        [`& .${classes.btn}`]: {
            color: 'inherit',
            marginRight: theme.spacing(1),
            '&:disabled': {
                opacity: 0.75,
                color: theme.palette.secondary.main,
                backgroundColor: theme.palette.common.white,
            },
            '&:hover': {
                color: theme.palette.secondary.main,
                backgroundColor: theme.palette.common.white,
            },
        },
        [`& .${classes.btnContainer}`]: {
            [theme.breakpoints.down('md')]: {
                marginTop: theme.spacing(1),
            },
        },
    };
});

const LaneSearch = ({
    open,
    onClose,
}: {
    open: boolean;
    onClose: () => void;
}): React.JSX.Element => {
    const [searchParams] = useSearchParams();
    const navigate = useNavigate();

    const [origin, setOrigin] = useState<AreaLabel | null>(null);
    const [destination, setDestination] = useState<AreaLabel | null>(null);
    const [isFilterPanelOpen, setIsFilterPanelOpen] = useState(false);

    const filters = useAppSelector((state) => { return state.filters.searchFilters; });

    const originParam = searchParams.get('origin');
    const destParam = searchParams.get('dest');

    const dirty = (originParam ? originParam !== origin?.area_id : Boolean(origin)) ||
        (destParam ? destParam !== destination?.area_id : Boolean(destination));

    useEffect(() => {
        const originArea = areas.find((area) => {
            return area.area_id === originParam;
        });

        const destinationArea = areas.find((area) => {
            return area.area_id === destParam;
        });

        setOrigin(originArea || null);
        setDestination(destinationArea || null);
    }, [originParam, destParam]);

    const swapOriginDestination = (): void => {
        const newOrigin = destination;
        const newDestination = origin;
        setOrigin(newOrigin);
        setDestination(newDestination);
    };

    const search = (): void => {
        if (origin && destination) {
            navigate(buildLaneLink(origin.area_id, destination.area_id, filters));
        } else if (origin) {
            navigate(buildAreaLink(origin.area_id, filters));
        } else if (destination) {
            navigate(buildAreaLink(destination.area_id, filters));
        } else {
            toast.warning('Origin or destination is required to search');
            return;
        }

        onClose();
    };

    const openFilterPanel = (): void => {
        setIsFilterPanelOpen(true);
    };

    const closeFilterPanel = (): void => {
        setIsFilterPanelOpen(false);
    };

    return (
        <Fragment>
            <Collapse in={open} unmountOnExit>
                <StyledGrid
                    container
                    className={classes.container}
                    alignItems='center'
                >
                    <Grid item xs={12} sm lg='auto'>
                        <SelectAutocomplete
                            key={origin?.area_id}
                            label='Origin'
                            autocompleteClasses={{
                                root: classes.selectField,
                            }}
                            value={origin}
                            options={areas}
                            multiple={false}
                            getOptionLabel={(option): string => {
                                return option?.area_name || '';
                            }}
                            isOptionEqualToValue={(option, value): boolean => {
                                return option?.area_id === value?.area_id;
                            }}
                            formatChipContent={undefined}
                            onChange={(event, newOrigin): void => {
                                setOrigin(newOrigin);
                            }}
                        />
                    </Grid>
                    <Grid
                        item
                        xs={12}
                        sm='auto'
                        className={classes.swapContainer}
                    >
                        <Tooltip title='Swap Origin and Destination'>
                            <IconButton
                                className={classes.icon}
                                size='large'
                                onClick={swapOriginDestination}
                            >
                                <Icon path={mdiSwapHorizontal} size={1} />
                            </IconButton>
                        </Tooltip>
                    </Grid>
                    <Grid item xs={12} sm lg='auto'>
                        <SelectAutocomplete
                            key={destination?.area_id}
                            label='Destination'
                            autocompleteClasses={{
                                root: classes.selectField,
                            }}
                            value={destination}
                            options={areas}
                            multiple={false}
                            getOptionLabel={(option): string => {
                                return option?.area_name || '';
                            }}
                            isOptionEqualToValue={(option, value): boolean => {
                                return option?.area_id === value?.area_id;
                            }}
                            formatChipContent={undefined}
                            onChange={(event, newDestination): void => {
                                setDestination(newDestination);
                            }}
                        />
                    </Grid>
                    <Grid item xs={12} md>
                        <Grid
                            container
                            className={classes.btnContainer}
                            sx={{
                                justifyContent: 'flex-end',
                            }}
                        >
                            <Grid item xs='auto'>
                                <Tooltip
                                    title={!dirty ? 'Change Origin or Destination to Search' : 'Search'}
                                >
                                    <span>
                                        <Button
                                            variant='outlined'
                                            aria-label='Search Lanes'
                                            className={classes.btn}
                                            size='medium'
                                            color='inherit'
                                            onClick={search}
                                            disabled={!dirty}
                                        >
                                            <Icon path={mdiMagnify} size={1} />
                                            SEARCH
                                        </Button>
                                    </span>
                                </Tooltip>
                            </Grid>
                            <Grid item xs='auto'>
                                <Tooltip title='Filters'>
                                    <Button
                                        className={classes.btn}
                                        onClick={openFilterPanel}
                                        size='medium'
                                        arial-label='Additional Search Filters'
                                        startIcon={<Icon path={mdiFilterVariant} size={1} />}
                                    >
                                        FILTERS
                                    </Button>
                                </Tooltip>
                            </Grid>
                        </Grid>
                    </Grid>
                </StyledGrid>
            </Collapse>
            <SearchFilterDrawer
                isOpen={isFilterPanelOpen}
                onClose={closeFilterPanel}
            />
        </Fragment>
    );
};

export default LaneSearch;
